import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { createCoreboundRenderer } from './createCoreboundRenderer';
import { observeElementSize } from '../../utils/observeElementSize';
import RelicIcon from './RelicIcon';
import { createTreasureSlashScene } from './treasureSlashScene';
import { TREASURE_RARITY_COLORS } from './treasureReveal';
import { drawCoreboundIcon } from './coreboundIconArt';
import { usesMobileRendering } from './mobileRendering';
import { getBoardPixelRatio } from './boardRenderBudget';
import { disposeCanvasTexture } from './sceneResources';
import { getLabelContext, createLabelTexture } from './labelTextures';

export { TREASURE_RARITY_COLORS } from './treasureReveal';

const TREASURE_REVEAL_TIMINGS = {
  slice: 1270,
  unlock: 1600,
  rise: 4000,
  reveal: 5600,
  complete: 7200,
};
const SPECIAL_TREASURE_REVEAL_TIMINGS = {
  slice: 1450,
  unlock: 1800,
  rise: 4500,
  reveal: 6700,
  complete: 8900,
};

export const getTreasureRevealTimings = (category) =>
  category === 'Special'
    ? SPECIAL_TREASURE_REVEAL_TIMINGS
    : TREASURE_REVEAL_TIMINGS;

const ease = (value) => {
  const p = THREE.MathUtils.clamp(value, 0, 1);
  return p * p * (3 - 2 * p);
};

const makeTexture = (draw) => {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const context = getLabelContext(canvas);
  if (!context) return null;
  draw(context);
  const texture = createLabelTexture(canvas);
  return texture;
};

const makeGlowTexture = () =>
  makeTexture((context) => {
    const gradient = context.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(0.15, '#ffffffaa');
    gradient.addColorStop(0.5, '#ffffff22');
    gradient.addColorStop(1, '#ffffff00');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 256);
  });

const makeGlyphTexture = (relicId) =>
  makeTexture((context) => {
    if (relicId === 'ghostBump') {
      const ghostGradient = context.createLinearGradient(76, 38, 186, 220);
      ghostGradient.addColorStop(0, '#f4efff');
      ghostGradient.addColorStop(0.55, '#c9c1ff');
      ghostGradient.addColorStop(1, '#75e6ee');
      context.beginPath();
      context.moveTo(128, 24);
      context.bezierCurveTo(73, 24, 48, 70, 48, 125);
      context.lineTo(48, 213);
      context.quadraticCurveTo(48, 229, 62, 220);
      context.lineTo(91, 201);
      context.lineTo(117, 219);
      context.quadraticCurveTo(128, 226, 139, 219);
      context.lineTo(165, 201);
      context.lineTo(194, 220);
      context.quadraticCurveTo(208, 229, 208, 213);
      context.lineTo(208, 125);
      context.bezierCurveTo(208, 70, 183, 24, 128, 24);
      context.closePath();
      context.fillStyle = ghostGradient;
      context.shadowColor = '#b7a3ff';
      context.shadowBlur = 22;
      context.fill();
      context.shadowBlur = 0;
      context.strokeStyle = '#ffffff';
      context.lineWidth = 5;
      context.stroke();
      context.fillStyle = '#30285c';
      context.beginPath();
      context.ellipse(101, 123, 9, 14, 0, 0, Math.PI * 2);
      context.ellipse(155, 123, 9, 14, 0, 0, Math.PI * 2);
      context.fill();
      context.strokeStyle = '#554b8a';
      context.lineWidth = 5;
      context.lineCap = 'round';
      context.beginPath();
      context.moveTo(113, 151);
      context.quadraticCurveTo(128, 164, 143, 151);
      context.stroke();
      context.fillStyle = '#76e7ef';
      context.beginPath();
      context.moveTo(128, 76);
      context.lineTo(137, 95);
      context.lineTo(157, 98);
      context.lineTo(143, 112);
      context.lineTo(146, 132);
      context.lineTo(128, 122);
      context.lineTo(110, 132);
      context.lineTo(113, 112);
      context.lineTo(99, 98);
      context.lineTo(119, 95);
      context.closePath();
      context.fill();
      return;
    }
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = 'bold 130px monospace';
    context.fillStyle = '#160c25';
    context.shadowColor = '#ffffff';
    context.shadowBlur = 12;
    drawCoreboundIcon(context, relicId, '#160c25', 35, 35, 186);
  });

export const createTreasureChestModel = (scene, glowTexture) => {
  const chest = new THREE.Group();
  chest.position.y = -1.2;
  scene.add(chest);
  const metal = new THREE.MeshStandardMaterial({
    color: 0x151627,
    metalness: 0.72,
    roughness: 0.3,
  });
  const panel = new THREE.MeshStandardMaterial({
    color: 0x261c36,
    metalness: 0.56,
    roughness: 0.42,
  });
  const gold = new THREE.MeshStandardMaterial({
    color: 0xd0a75b,
    metalness: 0.78,
    roughness: 0.24,
  });
  const seal = new THREE.MeshStandardMaterial({
    color: 0x9870ff,
    emissive: 0x8350ff,
    emissiveIntensity: 1.1,
    metalness: 0.25,
    roughness: 0.3,
  });
  const box = (parent, size, position, material = metal) => {
    const [width, height, depth] = size;
    const bevel = Math.min(0.045, width / 4, height / 4, depth / 4);
    const x = width / 2 - bevel;
    const y = height / 2 - bevel;
    const shape = new THREE.Shape();
    shape.moveTo(-x, -y);
    shape.lineTo(x, -y);
    shape.lineTo(x, y);
    shape.lineTo(-x, y);
    shape.closePath();
    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: depth - bevel * 2,
      bevelEnabled: true,
      bevelSegments: 2,
      steps: 1,
      bevelSize: bevel,
      bevelThickness: bevel,
    });
    geometry.translate(0, 0, -(depth - bevel * 2) / 2);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...position);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
  };

  // Separate walls leave an actual hollow cavity underneath the hinged lid.
  box(chest, [3.05, 0.18, 1.8], [0, 0.12, 0], gold).name = 'chest-base';
  // Keep the body above the base: their coplanar front faces used to fight
  // over the same depth pixels as the camera moved.
  box(chest, [2.95, 1.02, 0.16], [0, 0.7, 0.8]).name = 'chest-front';
  box(chest, [2.95, 1.02, 0.16], [0, 0.7, -0.8]);
  box(chest, [0.16, 1.02, 1.55], [-1.4, 0.7, 0]);
  box(chest, [0.16, 1.02, 1.55], [1.4, 0.7, 0]);
  box(chest, [2.5, 0.75, 0.035], [0, 0.65, 0.91], panel);
  box(chest, [2.6, 0.1, 1.45], [0, 0.24, 0], panel);
  [-1.43, 1.43].forEach((x) => {
    [-0.84, 0.84].forEach((z) => {
      box(chest, [0.18, 1.2, 0.18], [x, 0.66, z], gold);
    });
  });
  [-0.9, 0.9].forEach((x) => {
    box(chest, [0.12, 0.98, 0.08], [x, 0.66, 0.95], gold);
  });
  [0.31, 1.05].forEach((y) => {
    box(chest, [2.8, 0.025, 0.03], [0, y, 0.95], seal);
  });
  for (let index = 0; index < 8; index += 1) {
    const rune = box(
      chest,
      [0.09, 0.15, 0.025],
      [(index - 3.5) * 0.28, 0.66, 0.955],
      seal
    );
    rune.rotation.z = index % 2 ? Math.PI / 4 : -Math.PI / 4;
  }

  const lid = new THREE.Group();
  lid.position.set(0, 1.2, -0.85);
  chest.add(lid);
  box(lid, [3.1, 0.3, 1.85], [0, 0.1, 0.85]);
  box(lid, [2.62, 0.14, 1.3], [0, 0.28, 0.85], panel);
  [-0.94, 0.94].forEach((x) => {
    box(lid, [0.15, 0.06, 1.85], [x, 0.28, 0.85], gold);
  });
  [-0.06, 1.75].forEach((z) => {
    box(lid, [3.1, 0.06, 0.12], [0, 0.28, z], gold);
  });
  const crest = new THREE.Mesh(
    new THREE.TorusGeometry(0.32, 0.025, 6, 40),
    seal
  );
  crest.rotation.x = -Math.PI / 2;
  crest.position.set(0, 0.38, 0.85);
  lid.add(crest);
  const innerSigil = new THREE.Mesh(new THREE.OctahedronGeometry(0.21), gold);
  innerSigil.scale.y = 0.2;
  innerSigil.position.set(0, 0.38, 0.85);
  lid.add(innerSigil);

  const locks = [-1, 1].map((side) => {
    const lock = new THREE.Mesh(
      new THREE.TorusGeometry(0.22, 0.05, 8, 24, Math.PI),
      gold
    );
    lock.position.set(0, 0.76, 1.02);
    lock.rotation.z = side < 0 ? Math.PI : 0;
    chest.add(lock);
    return { lock, side };
  });
  const seam = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: glowTexture,
      color: 0x956aff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  seam.position.set(0, 1.18, 0.2);
  seam.scale.set(4, 1.2, 1);
  chest.add(seam);
  return { chest, lid, seal, seam, locks };
};

const TreasureChestScene = ({ relic, rarity, reduceMotion }) => {
  const mobileRendering = usesMobileRendering();
  const mountRef = useRef(null);
  const [webglAvailable, setWebglAvailable] = useState(true);
  const revealRarity = relic.category || relic.rarity || rarity || 'Common';
  const timings = getTreasureRevealTimings(revealRarity);
  const color =
    TREASURE_RARITY_COLORS[revealRarity] || TREASURE_RARITY_COLORS.Common;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    let renderer;
    let graphicsLost = false;
    try {
      renderer = createCoreboundRenderer({
        alpha: true,
        antialias: true,
      });
    } catch {
      renderer?.dispose();
      setWebglAvailable(false);
      return undefined;
    }
    setWebglAvailable(true);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050511, 0.035);
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 60);
    const rareColor = new THREE.Color(color);
    const mysteryColor = new THREE.Color(0x9870ff);
    const glowTexture = makeGlowTexture();
    const { chest, lid, seal, seam, locks } = createTreasureChestModel(
      scene,
      glowTexture
    );
    scene.add(new THREE.HemisphereLight(0x989acb, 0x121024, 2));
    const key = new THREE.DirectionalLight(0xffead1, 4);
    key.position.set(-3, 6, 5);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = -5;
    key.shadow.camera.right = 5;
    key.shadow.camera.top = 5;
    key.shadow.camera.bottom = -5;
    key.shadow.normalBias = 0.025;
    scene.add(key);
    const rim = new THREE.PointLight(0x8b50ff, 18, 12);
    rim.position.set(2, 2, -3);
    scene.add(rim);
    const treasureLight = new THREE.PointLight(color, 0, 9, 1.8);
    treasureLight.position.set(0, 0.3, 0.4);
    scene.add(treasureLight);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(40, 40),
      new THREE.MeshStandardMaterial({
        color: 0x0b0b17,
        metalness: 0.28,
        roughness: 0.65,
      })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -1.46;
    ground.receiveShadow = true;
    scene.add(ground);
    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(2.3, 2.55, 0.2, 64),
      new THREE.MeshStandardMaterial({
        color: 0x171329,
        metalness: 0.55,
        roughness: 0.4,
      })
    );
    pedestal.position.y = -1.34;
    pedestal.receiveShadow = true;
    scene.add(pedestal);
    const circles = [2.4, 2.65, 3.0].map((radius, index) => {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(radius, 0.012, 4, 96),
        new THREE.MeshBasicMaterial({
          color: 0x8651df,
          transparent: true,
          opacity: 0.24 + index * 0.08,
        })
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -1.22 - index * 0.06;
      scene.add(ring);
      return ring;
    });

    const sealArcs = [0, 1].map((side) => {
      const material = new THREE.MeshBasicMaterial({
        color: 0xb98aff,
        transparent: true,
        opacity: 0.76,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const arc = new THREE.Mesh(
        new THREE.TorusGeometry(0.34, 0.018, 8, 48, Math.PI),
        material
      );
      arc.position.set(0, 0.76, 1.13);
      arc.rotation.z = side * Math.PI;
      chest.add(arc);
      return { arc, material, side };
    });

    const slashEffects = createTreasureSlashScene(chest, glowTexture, color);

    const artifact = new THREE.Group();
    artifact.name = 'treasure-artifact';
    scene.add(artifact);
    const crystal = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.4),
      new THREE.MeshPhysicalMaterial({
        // Let the facets catch light without bleaching the rarity tint.
        color: rareColor.clone().multiplyScalar(0.12),
        emissive: color,
        emissiveIntensity: 0.6,
        metalness: 0.35,
        roughness: 0.2,
        clearcoat: 0.15,
        specularIntensity: 0.15,
        toneMapped: false,
      })
    );
    crystal.scale.set(0.85, 1.45, 0.85);
    crystal.name = 'treasure-prism-shard';
    artifact.add(crystal);
    const cage = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.67, 0),
      new THREE.MeshBasicMaterial({
        color,
        wireframe: true,
        transparent: true,
        opacity: 0.6,
      })
    );
    artifact.add(cage);
    const halo = new THREE.Mesh(
      new THREE.TorusGeometry(0.79, 0.016, 6, 72),
      new THREE.MeshBasicMaterial({ color })
    );
    halo.rotation.x = 1.1;
    artifact.add(halo);
    const artifactGlow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture,
        color,
        transparent: true,
        opacity: 0.55,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    artifactGlow.scale.set(2.2, 2.2, 1);
    artifact.add(artifactGlow);
    const glyphTexture = makeGlyphTexture(relic.id);
    const glyph = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glyphTexture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      })
    );
    glyph.scale.set(0.65, 0.65, 1);
    glyph.position.set(0, 0, 0);
    glyph.renderOrder = 3;
    artifact.add(glyph);

    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(1.2, 0.15, 3.6, 32, 1, true),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    beam.position.set(0, 1.7, 0);
    scene.add(beam);
    const particleCount = 140;
    const positions = new Float32Array(particleCount * 3);
    const particlesGeometry = new THREE.BufferGeometry();
    particlesGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3)
    );
    const particles = new THREE.Points(
      particlesGeometry,
      new THREE.PointsMaterial({
        color,
        size: 0.055,
        map: glowTexture,
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    scene.add(particles);
    const mist = Array.from({ length: 6 }, (_, index) => {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: glowTexture,
          color: 0x633cb7,
          transparent: true,
          opacity: 0.08,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      sprite.scale.set(3, 0.75, 1);
      sprite.position.set((index - 2.5) * 0.9, -0.9, -0.9 - (index % 2));
      scene.add(sprite);
      return sprite;
    });

    const startedAt = performance.now();
    let frame;
    const render = (now) => {
      const time = reduceMotion ? timings.complete : now - startedAt;
      const seconds = time / 1000;
      const opening = ease(
        (time - timings.unlock) / (timings.rise - timings.unlock)
      );
      const rising = ease(
        (time - timings.rise) / (timings.reveal - timings.rise)
      );
      const revealed = ease((time - timings.reveal) / 800);
      const lockBreak = ease((time - timings.slice - 260) / 520);
      const lockImpact = slashEffects.update({
        time,
        sliceAt: timings.slice,
        impactAt: timings.unlock,
        reduceMotion,
      });
      const orbit = ease(time / timings.complete);
      const radius = (7.2 - orbit * 0.6) / Math.min(1, camera.aspect / 0.85);
      const angle = 0.5 - orbit * 0.2;
      camera.position.set(
        Math.sin(angle) * radius,
        2.8 - orbit * 0.4,
        Math.cos(angle) * radius
      );
      camera.position.x += Math.sin(time * 0.12) * lockImpact * 0.07;
      camera.position.y += Math.cos(time * 0.15) * lockImpact * 0.045;
      camera.zoom = 1 + lockImpact * 0.06;
      camera.lookAt(0, 0.15, 0);
      camera.updateProjectionMatrix();
      lid.rotation.x = -opening * Math.PI * 0.61;
      seal.emissiveIntensity =
        0.6 + (1 - opening) * (1 + Math.sin(seconds * 5) * 0.45);
      seam.material.opacity = (0.12 + opening * 0.4) * (1 - revealed * 0.65);
      locks.forEach(({ lock, side }) => {
        lock.position.x = side * lockBreak * 1.15;
        lock.position.y = 0.76 + lockBreak * 0.6 - lockBreak * lockBreak * 0.8;
        lock.position.z = 1.02 + lockBreak * 0.6;
        lock.rotation.z = (side < 0 ? Math.PI : 0) + side * lockBreak * 3.4;
        lock.scale.setScalar(1 - lockBreak * 0.42);
        lock.visible = lockBreak < 0.99;
      });
      sealArcs.forEach(({ arc, material, side }) => {
        arc.position.x = side ? lockBreak * 0.42 : -lockBreak * 0.42;
        arc.position.y = 0.76 + lockBreak * (side ? -0.06 : 0.06);
        arc.position.z = 1.13 + lockBreak * 0.2;
        arc.rotation.z = side * Math.PI + (side ? 1 : -1) * lockBreak * 0.4;
        arc.scale.setScalar(1 + lockBreak * 0.12);
        material.opacity = 0.76 * (1 - lockBreak);
        arc.visible = lockBreak < 1;
        material.color.copy(mysteryColor).lerp(rareColor, opening);
      });
      artifact.visible = rising > 0.015;
      artifact.position.y =
        -0.35 +
        rising * 2.15 +
        (reduceMotion ? 0 : Math.sin(seconds * 2) * 0.08 * rising);
      artifact.scale.setScalar(0.45 + rising * 0.55);
      crystal.rotation.set(0.15, seconds * 0.6, 0.12);
      cage.rotation.set(seconds * 0.3, -seconds * 0.35, 0.25);
      halo.rotation.z = seconds * 0.5;
      glyph.material.opacity = revealed;
      // The shard must carry its actual rarity from the first visible frame.
      crystal.material.emissive.copy(rareColor);
      artifactGlow.material.color.copy(rareColor);
      rim.color.copy(mysteryColor).lerp(rareColor, rising);
      treasureLight.intensity = opening * (9 + rising * 7) + lockImpact * 20;
      treasureLight.color.copy(rareColor);
      treasureLight.position.y = 0.1 + rising * 1.2;
      beam.material.opacity = opening * 0.07 * (1 - revealed * 0.75);
      beam.scale.set(
        0.3 + opening * 0.7,
        0.05 + opening * 0.95,
        0.3 + opening * 0.7
      );
      beam.position.y = -0.1 + beam.scale.y * 1.8;
      circles.forEach((ring, index) => {
        ring.material.opacity =
          0.22 + Math.sin(seconds * 2 - index) * 0.06 + opening * 0.15;
        ring.material.color.copy(mysteryColor).lerp(rareColor, revealed);
      });
      mist.forEach((sprite, index) => {
        sprite.position.x = Math.sin(seconds * 0.3 + index * 1.6) * 2.5;
        sprite.material.opacity = 0.06 + opening * 0.06;
      });
      for (let index = 0; index < particleCount; index += 1) {
        const a = index * 2.39996 + seconds * (0.16 + rising * 0.25);
        const p = (index / particleCount + seconds * 0.1) % 1;
        const distance = (1.6 - p * 0.7) * (0.6 + opening * 0.4);
        positions[index * 3] = Math.cos(a) * distance;
        positions[index * 3 + 1] = -1 + p * (1 + opening * 3.8);
        positions[index * 3 + 2] = Math.sin(a) * distance;
      }
      particlesGeometry.attributes.position.needsUpdate = true;
      particles.material.opacity = 0.2 + opening * 0.65;
      renderer.render(scene, camera);
    };
    const animate = (now) => {
      if (graphicsLost) return;
      frame = requestAnimationFrame(animate);
      render(now);
    };
    const resize = () => {
      if (graphicsLost) return;
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      const ratio = getBoardPixelRatio(width, height, window.devicePixelRatio);
      renderer.setDrawingBufferSize(width, height, ratio);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      render(performance.now());
    };
    const onContextLost = (event) => {
      event.preventDefault();
      graphicsLost = true;
      cancelAnimationFrame(frame);
      renderer.dispose();
      renderer.domElement.width = 1;
      renderer.domElement.height = 1;
      setWebglAvailable(false);
    };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost);
    const stopObservingSize = observeElementSize(mount, resize);
    if (!reduceMotion) frame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frame);
      stopObservingSize();
      renderer.domElement.removeEventListener(
        'webglcontextlost',
        onContextLost
      );
      const geometries = new Set();
      const materials = new Set();
      const textures = new Set([glowTexture, glyphTexture]);
      scene.traverse((object) => {
        object.shadow?.dispose();
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) {
          (Array.isArray(object.material)
            ? object.material
            : [object.material]
          ).forEach((material) => {
            materials.add(material);
            if (material.map) textures.add(material.map);
          });
        }
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
      textures.forEach((texture) => {
        if (texture) disposeCanvasTexture(texture);
      });
      if (!graphicsLost) {
        renderer.dispose();
        renderer.forceContextLoss();
      }
      renderer.domElement.width = 1;
      renderer.domElement.height = 1;
      if (mount.contains(renderer.domElement))
        mount.removeChild(renderer.domElement);
    };
  }, [relic.id, relic.icon, color, reduceMotion, timings, mobileRendering]);

  return (
    <div
      className={`coreboundTreasureViewport ${
        reduceMotion ? 'is-reduced' : ''
      } ${webglAvailable ? '' : 'is-fallback'} ${
        revealRarity === 'Special' ? 'is-special' : ''
      } ${mobileRendering ? 'is-mobile-rendering' : ''}`}
      aria-hidden="true"
      style={{
        '--treasure-color': color,
        '--chest-slash-delay': `${timings.slice}ms`,
        '--chest-impact-delay': `${timings.unlock}ms`,
      }}
    >
      <div ref={mountRef} className="coreboundTreasureCanvas" />
      {!webglAvailable && (
        <div className="coreboundTreasureFallback">
          <div className="coreboundFallbackChest">
            <div className="coreboundFallbackChestFront">◇</div>
            <div className="coreboundFallbackChestSide" />
            <div className="coreboundFallbackChestLid" />
            <span className="coreboundFallbackSlash" />
          </div>
          <div className="coreboundFallbackRelic">
            <RelicIcon relicId={relic.id} icon={relic.icon} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TreasureChestScene;
